import React, { useEffect, useMemo, useState, forwardRef } from 'react';
import { optionsWithSelection, SelectSearch } from '~/ui/components';
import { debounce } from 'lodash';
import { useAwaitControl } from 'react-redux-await-control';
import { fetchTicketBanks } from '~/store/utilities/actions';

export type BankNameSelectProps = {
  value?: string | null | undefined;
  onChange?: (value: string) => void;
};

export const BankNameSelect = forwardRef<HTMLDivElement, BankNameSelectProps>(({ value, onChange }, ref) => {
  const [currentValue, setCurrentValue] = useState<{ value: string; label: string }>(null);
  const [searchString, setSearchString] = useState('');

  const awaitControl = useAwaitControl(fetchTicketBanks);
  const isLoading = awaitControl.isRunning();
  const banksList = awaitControl.result();

  const normalizedOptions = useMemo(
    () => banksList?.map((bank) => ({ name: bank.bankName, id: bank.bankName })) || [],
    [banksList],
  );

  const options = useMemo(() => {
    if (!searchString?.length) {
      return normalizedOptions;
    }

    return normalizedOptions?.filter((data) => data.name?.toLowerCase().includes(searchString.toLowerCase().trim()));
  }, [normalizedOptions, searchString]);

  const handleOnChange = (option: { value: string; label: string }) => {
    setCurrentValue(option);

    if (onChange) {
      onChange(option?.value);
    }
  };

  const handleSearch = debounce((searchValue) => {
    setSearchString(searchValue);
  }, 500);

  useEffect(() => {
    if (!banksList?.length) {
      awaitControl.start();
    }
  }, []);

  return (
    <SelectSearch
      ref={ref}
      searchValue={searchString}
      placeholder="Consignatária"
      width={180}
      value={value ? { value } : null}
      onSearch={handleSearch}
      onChange={handleOnChange}
      loading={isLoading}
      options={optionsWithSelection(options, currentValue)}
      onClear={() => setSearchString('')}
    />
  );
});
